import { httpClient } from './httpClient'
import { COUNTRIES_MAP } from '@/data/countriesLookup'
import type {
  ComparisonRequest,
  ComparisonApiResponse,
  SingleSubjectRequest,
  SearchRequest,
  SearchApiResponse,
  ListRequest,
  ListApiResponse,
  SearchableDimensionType,
  ListDimensionType,
} from '@/types/comparison'

/**
 * Comparison API Service
 *
 * Provides methods for multi-dimensional comparison:
 * - compare() - Compare two subjects (A vs B)
 * - searchSubjects() - Autocomplete search for subjects
 * - listSubjects() - Get fixed list of subjects
 *
 * Backend: app/Http/Controllers/V11/Discovery/FunnelController.php
 * Backend: app/Http/Controllers/V11/Discovery/SubjectSearchController.php
 */
class ComparisonService {
  /**
   * Compare two subjects (A vs B).
   *
   * POST /api/v11/discovery/compare
   *
   * @param request - Comparison request payload
   * @returns Comparison result with aggregations, overlap, and sections
   *
   * @example
   * ```typescript
   * const result = await comparisonService.compare({
   *   subjectA: { type: 'platform', id: 1 },  // Instagram
   *   subjectB: { type: 'platform', id: 2 },  // X
   * })
   * ```
   */
  async compare(request: ComparisonRequest): Promise<ComparisonApiResponse> {
    const response = await httpClient.post<ComparisonApiResponse>(
      '/api/v11/discovery/compare',
      request
    )
    return response
  }

  /**
   * Compare a single subject (for progressive loading).
   *
   * POST /api/v11/discovery/compare
   *
   * Sends only one subject (A or B). Backend returns the standard
   * ComparisonApiResponse with only the requested subject populated.
   *
   * @param request - Single subject request payload
   * @returns Standard comparison response (with only one subject populated)
   */
  async compareSingle(request: SingleSubjectRequest): Promise<ComparisonApiResponse> {
    const response = await httpClient.post<ComparisonApiResponse>(
      '/api/v11/discovery/compare',
      request
    )
    return response
  }

  /**
   * Search for subjects by type and query (autocomplete).
   *
   * GET /api/v11/discovery/compare/search?type={type}&q={query}&limit={limit}
   *
   * @param params - Search parameters
   * @returns Array of matching subjects
   *
   * @example
   * ```typescript
   * const results = await comparisonService.searchSubjects({
   *   type: 'brand',
   *   q: 'nike',
   *   limit: 10,
   * })
   * // Returns: [{ id: 1, name: 'Nike', subtitle: 'Sportswear', icon: '...' }, ...]
   * ```
   */
  async searchSubjects(params: SearchRequest): Promise<SearchApiResponse> {
    const response = await httpClient.get<SearchApiResponse>(
      '/api/v11/discovery/compare/search',
      { params }
    )
    return response
  }

  /**
   * Get fixed list of subjects by type.
   *
   * GET /api/v11/discovery/compare/lists?type={type}
   *
   * @param params - List parameters
   * @returns Array of all available subjects for the dimension
   *
   * @example
   * ```typescript
   * const platforms = await comparisonService.listSubjects({
   *   type: 'platform',
   * })
   * // Returns: [{ id: 1, name: 'Instagram', ... }, { id: 2, name: 'X', ... }]
   * ```
   */
  async listSubjects(params: ListRequest): Promise<ListApiResponse> {
    const response = await httpClient.get<ListApiResponse>(
      '/api/v11/discovery/compare/lists',
      { params }
    )

    // Optimize country lists: Backend returns { id, count }, frontend maps names
    if (params.type === 'country' && response.data) {
      const mappedData = response.data.map((item: any) => ({
        id: item.id,
        name: COUNTRIES_MAP[item.id]?.name || `Country ${item.id}`,
        subtitle: COUNTRIES_MAP[item.id]?.iso || undefined,
        count: item.count,
      }))
      return { ...response, data: mappedData }
    }

    return response
  }

  /**
   * Helper: Check if a dimension type is searchable (supports autocomplete).
   *
   * @param type - Dimension type
   * @returns True if searchable, false if fixed list
   */
  isSearchableDimension(type: string): type is SearchableDimensionType {
    return ['brand', 'team', 'hashtag', 'channel', 'author', 'post'].includes(type)
  }

  /**
   * Helper: Check if a dimension type is a fixed list.
   *
   * @param type - Dimension type
   * @returns True if fixed list, false if searchable
   */
  isListDimension(type: string): type is ListDimensionType {
    return ['platform', 'country', 'ageGroup', 'emotion', 'category', 'persona'].includes(type)
  }
}

// Export singleton instance
export const comparisonService = new ComparisonService()
